
import React, { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { RefreshCw, Calendar, X } from 'lucide-react'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Area, AreaChart } from 'recharts'

// Custom Tooltip Component
const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-orange-200 rounded-lg shadow-md p-2 min-w-[90px]">
        <p className="text-xs font-medium text-gray-600 border-b border-gray-100 pb-1 mb-1">
          📅 {label}
        </p>
        <p className="text-sm font-semibold text-orange-600">
          💰 ${payload[0].value.toFixed(2)}
        </p>
      </div>
    );
  }
  return null;
};


interface EarningsChartProps {
  chartType: 'line' | 'bar' | 'area';
  onChartTypeChange: (type: 'line' | 'bar' | 'area') => void;
  onRefresh: () => void;
  selectedDateRange: string;
  onDateRangeChange?: (startDate: string, endDate: string) => void;
  onPresetDateRangeChange?: (range: string) => void;
  data: Array<{
    name: string;
    siparisler: number;
    kazanc: number;
    bonus: number;
  }>;
}

export default function EarningsChart({ 
  chartType, 
  onChartTypeChange, 
  onRefresh, 
  selectedDateRange,
  onDateRangeChange,
  onPresetDateRangeChange,
  data 
}: EarningsChartProps) {
  const [showCustomDate, setShowCustomDate] = useState(false)
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [isRefreshing, setIsRefreshing] = useState(false) // 👈 YENİ EKLEME

  const presetRanges = [
    'Son 7 gün',
    'Son 30 gün', 
    'Son 3 ay',
    'Son 6 ay',
    'Son 1 yıl',
    'Tüm zamanlar'
  ]

  const handleApplyCustomDate = () => {
    if (startDate && endDate && onDateRangeChange) {
      if (new Date(startDate) > new Date(endDate)) {
        alert('Başlangıç tarihi bitiş tarihinden sonra olamaz!')
        return
      }
      onDateRangeChange(startDate, endDate)
      setShowCustomDate(false)
    }
  }

  const handlePresetClick = (range: string) => {
    if (onPresetDateRangeChange) {
      onPresetDateRangeChange(range)
    }
  }

const handleRefreshClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    setIsRefreshing(true)
    onRefresh()
    
    // 1 saniye sonra loading'i kapat (görsel feedback için)
    setTimeout(() => {
      setIsRefreshing(false)
    }, 1000)
  }

  const isPresetSelected = (range: string) => {
 
    if (selectedDateRange.includes(' - ')) {
      return false
    }
    return selectedDateRange === range
  }

  const renderChart = () => {
    const commonProps = {
      data,
      children: [
        <CartesianGrid key="grid" strokeDasharray="3 3" stroke="#fed7aa" />,
        <XAxis key="xaxis" dataKey="name" stroke="#9a3412" />,
        <YAxis key="yaxis" stroke="#9a3412" />,
<Tooltip 
  key="tooltip"
  content={<CustomTooltip />}
/>
      ]
    };

    if (chartType === 'area') {
      return (
        <AreaChart {...commonProps}>
          <defs>
            <linearGradient id="colorBonus" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#f97316" stopOpacity={0.8}/>
              <stop offset="95%" stopColor="#f97316" stopOpacity={0.1}/>
            </linearGradient>
          </defs>
          {commonProps.children}
          <Area 
            type="monotone" 
            dataKey="bonus" 
            stroke="#f97316" 
            fillOpacity={1} 
            fill="url(#colorBonus)" 
            strokeWidth={2}
          />
        </AreaChart>
      );
    }
    
    if (chartType === 'line') {
      return (
        <LineChart {...commonProps}>
          {commonProps.children}
          <Line 
            type="monotone" 
            dataKey="bonus" 
            stroke="#f97316" 
            strokeWidth={3}
            dot={{ fill: '#f97316', strokeWidth: 2, r: 4 }}
            activeDot={{ r: 6, stroke: '#f97316', strokeWidth: 2 }}
          />
        </LineChart>
      );
    }
    

    return (
      <BarChart {...commonProps}>
        {commonProps.children}
        <Bar dataKey="bonus" fill="#f97316" radius={[4, 4, 0, 0]} />
      </BarChart>
    );
  };

  return (
    <Card className="glass border-orange-200 shadow-lg">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg font-semibold text-gray-800">
              Sipariş - Kazanç Grafiği
            </CardTitle>
            <CardDescription className="text-gray-600">
              {selectedDateRange}
            </CardDescription>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant={chartType === 'area' ? 'default' : 'outline'}
              size="sm"
              onClick={() => onChartTypeChange('area')}
              className="text-xs"
            >
              Area
            </Button>
            <Button
              variant={chartType === 'line' ? 'default' : 'outline'}
              size="sm"
              onClick={() => onChartTypeChange('line')}
              className="text-xs"
            >
              Line
            </Button>
            <Button
              variant={chartType === 'bar' ? 'default' : 'outline'}
              size="sm"
              onClick={() => onChartTypeChange('bar')}
              className="text-xs"
            >
              Bar
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRefreshClick}
              disabled={isRefreshing}
              className="text-xs"
            >
              <RefreshCw className={`w-3 h-3 ${isRefreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {/* Date Range Selector */}
        <div className="mt-4 space-y-3">
          {/* Preset Buttons */}
          <div className="flex flex-wrap gap-2">
            {presetRanges.map((range) => (
              <Button
                key={range}
                variant={isPresetSelected(range) ? 'default' : 'outline'}
                size="sm"
                onClick={() => handlePresetClick(range)}
                className="text-xs"
              >
                {range}
              </Button>
            ))}
            <Button
              variant={showCustomDate ? 'default' : 'outline'}
              size="sm"
              onClick={() => setShowCustomDate(!showCustomDate)}
              className="text-xs"
            >
              <Calendar className="w-3 h-3 mr-1" />
              Özel Tarih
            </Button>
          </div>

          {/* Custom Date Range Inputs */}
          {showCustomDate && (
            <div className="flex items-center gap-2 p-3 bg-orange-50 rounded-lg border border-orange-200">
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Başlangıç
                </label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="text-xs h-8"
                />
              </div>
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Bitiş
                </label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="text-xs h-8"
                />
              </div>
              <div className="flex gap-1 mt-5">
                <Button
                  size="sm"
                  onClick={handleApplyCustomDate}
                  disabled={!startDate || !endDate}
                  className="text-xs h-8 bg-orange-500 hover:bg-orange-600"
                >
                  Uygula
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setShowCustomDate(false)
                    setStartDate('')
                    setEndDate('')
                  }}
                  className="text-xs h-8"
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            {renderChart()}
          </ResponsiveContainer>
        </div>
        
        {/* Veri yoksa uyarı göster */}
        {(!data || data.length === 0) && (
          <div className="text-center py-8">
            <p className="text-gray-500 text-sm">
              Bu tarih aralığında veri bulunamadı
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  )
}